<script setup lang="ts">
import { ref } from "vue";

import { usePageStore } from "~/stores";

const props = defineProps<{
  styles?: string;
  disabled?: boolean;
  dataTypes?: string[] | ((types: readonly string[]) => boolean);
  disabledOverComponents?: HTMLElement[];
}>();

const emit = defineEmits<{
  drop: [files: File[]];
}>();

const pageStore = usePageStore();

const wrapper = ref<HTMLDivElement | null>(null);

const dragging = ref(false);
const dragCounter = ref(0);
const showOverlay = ref(true);

const updateShowOverlay = (event: DragEvent) => {
  const boundingRects = props.disabledOverComponents?.map((e) => e.getBoundingClientRect()) ?? [];
  if (!boundingRects.length) {
    return;
  }
  showOverlay.value = boundingRects.every(
    (rect) =>
      !(
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom
      )
  );
};

const handleDragEnter = (event: DragEvent) => {
  if (props.disabled || pageStore.isPublicView) {
    return;
  }
  dragCounter.value += 1;
  dragging.value = true;
  updateShowOverlay(event);
};

const handleDragMove = (event: DragEvent) => {
  if (!dragging.value) {
    return;
  }
  updateShowOverlay(event);
};

const handleDragLeave = () => {
  if (props.disabled || pageStore.isPublicView) {
    return;
  }
  dragCounter.value -= 1;
  if (dragCounter.value !== 0) {
    return;
  }
  dragging.value = false;
  showOverlay.value = true;
};

const handleDrop = (event: DragEvent) => {
  dragCounter.value = 0;
  dragging.value = false;
  const oldShowOverlay = showOverlay.value;
  showOverlay.value = true;
  if (props.disabled || pageStore.isPublicView || !oldShowOverlay) {
    return;
  }

  const files = Array.from(event.dataTransfer?.files || []);
  if (!files || files.length === 0) {
    return;
  }
  emit("drop", files);
};
</script>

<template>
  <div
    ref="wrapper"
    class="relative size-full"
    @dragenter.prevent.stop="handleDragEnter"
    @dragleave.prevent.stop="handleDragLeave"
    @dragover.prevent.stop="handleDragMove"
    @drop.prevent.stop="handleDrop">
    <slot name="main" />
    <div
      class="absolute left-0 top-0 z-20 size-full bg-md/80"
      :class="[dragging && showOverlay ? 'flex' : 'hidden', styles]"
      @dragenter.prevent.stop="handleDragEnter"
      @dragleave.prevent.stop="handleDragLeave"
      @dragover.prevent.stop="handleDragMove"
      @drop.prevent.stop="handleDrop">
      <slot name="dragOverlay" />
    </div>
  </div>
</template>
